import { useMemo, useState } from "react";
import { NumField } from "@/components/tool/CalcFields";
import { numField } from "@/lib/health/body-profile";
import { waterIntakeLitres } from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function WaterIntakeCalc() {
  const locale = getLocale();
  const [weight, setWeight] = useState("70");
  const [exercise, setExercise] = useState("30");

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.weightKg) setWeight(String(p.weightKg));
      if (p.exerciseMinPerDay) setExercise(String(p.exerciseMinPerDay));
    },
    collect: () => ({
      weightKg: numField(weight),
      exerciseMinPerDay: numField(exercise),
    }),
    deps: [weight, exercise],
  });

  const out = useMemo(() => {
    const w = Number(weight);
    const e = Number(exercise);
    if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(e)) return null;
    const litres = waterIntakeLitres(w, e);
    return { litres, cups: litres / 0.25, oz: litres * 33.814 };
  }, [weight, exercise]);

  const num = (n: number, d = 1) =>
    n.toLocaleString(locale, { maximumFractionDigits: d });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <NumField
          id="weight"
          label={m.h_weight_kg()}
          value={weight}
          onChange={setWeight}
          suffix="kg"
        />
        <NumField
          id="exercise"
          label={m.water_exercise()}
          value={exercise}
          onChange={setExercise}
          suffix="min"
        />
      </div>
      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.water_label()}
            </span>
            <span
              data-testid="water-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {num(out.litres, 2)} L
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.water_cups(), value: num(out.cups, 0) },
              { label: m.water_oz(), value: `${num(out.oz, 0)} oz` },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.h_hint()}</p>
      )}
      <BodyProfileHint {...profileSync} />
    </div>
  );
}
